import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
export function About() {
  const stats = [{
    eyebrow: 'CAREER SPAN',
    number: '40+',
    label: 'Years of Executive Leadership'
  }, {
    eyebrow: 'PUBLISHED WORK',
    number: '100+',
    label: 'Peer-Reviewed Publications & Textbooks'
  }, {
    eyebrow: 'ENTERPRISE BUILT',
    number: '12',
    label: 'Years Scaling ClearChoice Nationally'
  }, {
    eyebrow: 'GLOBAL REACH',
    number: '4',
    label: 'Continents of Keynote & Advisory Engagement'
  }];
  const highlights = ['Co-founded and scaled ClearChoice into a national multi-location healthcare platform', 'Drives board-level governance and enterprise growth across Med Tech and investment portfolios', 'Authored 100+ peer-reviewed publications and multiple landmark textbooks in oral surgery', 'Holds an Endowed Chair at the University of Utah School of Dentistry', 'Trusted advisor to boards, executive leadership, and global academic institutions'];
  return <section id="about" className="py-24 md:py-32 bg-cream" data-id="element-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-13">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-14">
          <div className="w-16 h-px bg-gold mb-6" data-id="element-15"></div>
          <h2 className="font-sans text-gold text-sm uppercase tracking-widest font-medium mb-4" data-id="element-16">
            About
          </h2>
          <h3 className="font-serif text-4xl md:text-5xl text-navy font-medium leading-tight max-w-3xl" data-id="element-17">
            Four Decades of Industry-Defining Leadership
          </h3>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24" data-id="element-18">
          {/* Left Column - Bio */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-7 space-y-6 font-sans text-charcoal/80 text-lg leading-relaxed font-light" data-id="element-19">
            <p data-id="element-20">
              A distinguished Oral and Maxillofacial Surgeon and globally
              recognized authority in implant dentistry, Dr. Ole T. Jensen has
              spent more than four decades shaping the frontier of clinical
              practice, academic medicine, and healthcare innovation. From
              pioneering osseointegration techniques in the operating room to
              chairing the board of a fast-growing MedTech enterprise, his
              career reflects a singular focus: elevating institutional
              performance and translating scientific breakthroughs into lasting
              commercial and clinical value.
            </p>
            <p data-id="element-21">
              As Co-Founder of ClearChoice Dental Implant Centers, Dr. Jensen
              architected one of the most successful commercialization stories
              in modern dental healthcare — building a national multi-site
              platform from concept to enterprise scale over twelve
              transformative years. His ability to translate clinical innovation
              into scalable operating models, durable brand equity, and superior
              patient outcomes has set the benchmark for governance and growth
              in specialty healthcare.
            </p>
            <p data-id="element-22">
              Today, as Founder & Chairman of Ditron Dental USA, Dr. Jensen
              brings independent board leadership, governance discipline, and
              strategic clarity to a rapidly evolving Med Tech landscape. He
              partners with executive teams on capital allocation, market
              expansion, regulatory strategy, and long-term value creation —
              operating at the intersection of clinical excellence, commercial
              performance, and stakeholder trust.
            </p>
            <p className="italic text-charcoal/70" data-id="element-23">
              Trusted by global institutions, peer-reviewed by the world's
              leading journals, and sought after as a keynote voice across four
              continents, Dr. Jensen continues to define what executive
              leadership looks like in highly regulated, innovation-driven
              industries.
            </p>

            {/* Executive Impact Highlights */}
            <div className="pt-8" data-id="element-24">
              <h4 className="font-sans text-gold text-xs uppercase tracking-widest font-semibold mb-6" data-id="element-25">
                Executive Impact Highlights
              </h4>
              <div className="space-y-4" data-id="element-26">
                {highlights.map((highlight, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1,
                duration: 0.4
              }} className="flex items-start gap-3" data-id="element-27">
                    <CheckCircle2Icon size={18} className="text-gold flex-shrink-0 mt-0.5" data-id="element-28" />
                    <p className="font-sans text-charcoal/80 text-base leading-relaxed" data-id="element-29">
                      {highlight}
                    </p>
                  </motion.div>)}
              </div>
            </div>

            <blockquote className="mt-12 border-l-2 border-gold pl-6 py-2" data-id="element-30">
              <p className="font-serif italic text-2xl text-navy leading-snug" data-id="element-31">
                <span className="text-gold text-3xl leading-none mr-1" data-id="element-32">"</span>
                Translating breakthrough science into widely adopted clinical
                protocols and scalable treatment models.
                <span className="text-gold text-3xl leading-none ml-1" data-id="element-33">"</span>
              </p>
            </blockquote>
          </motion.div>

          {/* Right Column - Premium Stats Cards */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="lg:col-span-5" data-id="element-34">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6" data-id="element-35">
              {stats.map((stat, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5 + index * 0.1,
              duration: 0.5
            }} className="relative bg-white p-8 border border-gold/20 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group" data-id="element-36">
                  {/* Mini gold corner accents */}
                  <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-gold group-hover:border-gold-bright transition-colors" data-id="element-37"></div>
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-gold group-hover:border-gold-bright transition-colors" data-id="element-38"></div>

                  <span className="font-sans text-gold text-[10px] uppercase tracking-widest font-semibold block mb-3" data-id="element-39">
                    {stat.eyebrow}
                  </span>
                  <span className="font-serif text-5xl md:text-6xl text-gold mb-4 block" data-id="element-40">
                    {stat.number}
                  </span>
                  <div className="w-12 h-px bg-gold/30 mb-4" data-id="element-41"></div>
                  <span className="font-sans text-navy uppercase tracking-widest text-sm font-medium leading-tight" data-id="element-42">
                    {stat.label}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}